import { tv, type VariantProps } from "tailwind-variants";

/*
 * Self-contained typography for the Header. Uses arbitrary px-derived values
 * (rem) so it does not depend on any app-level typography preset being loaded.
 *
 * rem calculation: px value / 16  (e.g. 14px / 16 = 0.875rem)
 * line-height values are unit-less ratios (line-height px / font-size px)
 */
const textVariants = tv({
  base: "font-sans leading-none tracking-normal text-current",
  variants: {
    variant: {
      bodyLg:
        "font-sans" +
        " text-[1rem] md:text-[1rem] xl:text-[1.125rem]" +
        " leading-[1.375] md:leading-[1.375] xl:leading-[1.33]" +
        " tracking-[0rem] md:tracking-[0rem] xl:tracking-[0rem]",
      bodyMd:
        "font-sans" +
        " text-[0.875rem] md:text-[1rem] xl:text-[1rem]" +
        " leading-[1.286] md:leading-[1.286] xl:leading-[1.286]" +
        " tracking-[0rem] md:tracking-[0rem] xl:tracking-[-0.006rem]",
      bodySm:
        "font-sans" +
        " text-[0.875rem] md:text-[0.875rem] xl:text-[0.875rem]" +
        " leading-[1.286] md:leading-[1.286] xl:leading-[1.286]" +
        " tracking-[0.031rem] md:tracking-[0.031rem] xl:tracking-[0.031rem]",
      labelSm:
        "font-mono uppercase" +
        " text-[0.6875rem] md:text-[0.6875rem] xl:text-[0.6875rem]" +
        " leading-[1.273] md:leading-[1.273] xl:leading-[1.273]" +
        " tracking-[0.094rem] md:tracking-[0.094rem] xl:tracking-[0.094rem]",
      h5:
        "font-sans" +
        " text-[1.125rem] md:text-[1.25rem] xl:text-[1.5rem]" +
        " leading-[1.111] md:leading-[1.1] xl:leading-[1.25]" +
        " tracking-[-0.031rem] md:tracking-[-0.031rem] xl:tracking-[-0.031rem]",
      btnLg:
        "font-sans" +
        " text-[0.875rem] md:text-[1rem] xl:text-[1rem]" +
        " leading-[0.929] md:leading-[0.938] xl:leading-[0.938]" +
        " tracking-[0rem] md:tracking-[0rem] xl:tracking-[-0.006rem]",
    },
    anchor: {
      true: "bg-linear-to-r relative cursor-pointer from-current to-current bg-[length:100%_0.05em] bg-[position:0%_100%] bg-no-repeat pb-1",
    },
    animateIn: { true: "", false: "" },
    animateOut: { true: "", false: "" },
    caret: { true: "", false: "" },
  },
  defaultVariants: {
    anchor: false,
    animateIn: false,
    animateOut: false,
    caret: false,
  },
  compoundVariants: [
    {
      anchor: true,
      animateIn: true,
      className:
        "bg-[length:0%_0.06em] bg-[position:100%_100%] transition-[background-size] duration-200 ease-out hover:bg-[length:100%_0.06em] hover:bg-[position:0%_100%]",
    },
    {
      anchor: true,
      animateOut: true,
      className:
        "bg-[length:100%_0.06em] bg-[position:0%_100%] transition-[background-size] duration-200 ease-out hover:bg-[length:0%_0.06em] hover:bg-[position:100%_100%]",
    },
    {
      anchor: true,
      caret: true,
      className:
        "relative after:absolute after:right-[-1.15em] after:top-1/2 after:block after:-translate-y-[45%] after:text-[0.7em] after:leading-[1.1] after:content-['▶']",
    },
  ],
});

type TextProps = React.HTMLAttributes<HTMLElement> &
  VariantProps<typeof textVariants> & { className?: string };

export function Text({
  children,
  className,
  anchor,
  animateIn,
  animateOut,
  caret,
  variant,
  ...rest
}: TextProps) {
  return (
    <span
      className={textVariants({
        anchor,
        animateIn,
        animateOut,
        caret,
        variant,
        className,
      })}
      {...rest}
    >
      {children}
    </span>
  );
}
